import React, { useState, useRef, useLayoutEffect, useImperativeHandle, forwardRef } from 'react';
import MultiSelectTicker from '../MultiSelectTicker';
import ScreenerSavedParams from '../ScreenerSavedParams';
import { BookmarkPlus, PlusCircle, AlertTriangle, Play, BookmarkIcon } from 'lucide-react';
import {
  intermediatePresets,
  buildIntermediateScenarios,
} from '../../utils/presetMappings';
import '../../styles/MobileScreenerForm.css';

const directionLabels = {
  buy_call:  { title: 'Bullish',      subtitle: 'Buy calls' },
  buy_put:   { title: 'Bearish',      subtitle: 'Buy puts' },
  sell_put:  { title: 'Neutral/bull', subtitle: 'Sell puts' },
  sell_call: { title: 'Neutral/bear', subtitle: 'Sell calls', warning: 'High risk — unlimited loss' },
};

const MobileScreenerIntermediateForm = forwardRef(({
  onAddToQueue,
  onAddAndRun,
  onSaveScenario,
  queueCount,
  loading,
  savedParams = [],
  onLoadParam,
  onDeleteParam,
  onAddParamToQueue,
}, ref) => {
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [direction, setDirection] = useState(null);
  const [selectedExpirations, setSelectedExpirations] = useState([]);
  const [selectedStrikes, setSelectedStrikes] = useState([]);
  const [isPresetSheetOpen, setIsPresetSheetOpen] = useState(false);

  // Pill-row refs for first-render scroll centering on Medium-Term / ATM
  const expirationRowRef = useRef(null);
  const strikeRowRef = useRef(null);

  // Center the anchor pill (data-default-pill) on first render for each row.
  // Pure scroll-position adjustment; nothing is selected.
  useLayoutEffect(() => {
    const centerAnchor = (container) => {
      if (!container) return;
      const anchor = container.querySelector('[data-default-pill="true"]');
      if (!anchor) return;
      const target = anchor.offsetLeft + anchor.offsetWidth / 2 - container.offsetWidth / 2;
      container.scrollLeft = Math.max(0, target);
    };
    centerAnchor(expirationRowRef.current);
    centerAnchor(strikeRowRef.current);
  }, []);

  const isPut = direction?.includes('put');

  const isFormValid = () =>
    selectedTickers.length > 0 &&
    direction !== null &&
    selectedExpirations.length > 0 &&
    selectedStrikes.length > 0;

  const buildScenarios = () => {
    if (!isFormValid()) return [];
    const isBullishPosition = direction === 'buy_call' || direction === 'sell_put';
    const moveSeed = isBullishPosition ? 'rally' : 'correction';
    return buildIntermediateScenarios(
      {
        expirations: selectedExpirations,
        strikes: selectedStrikes,
        speeds: ['balanced'],
        moves: [moveSeed],
      },
      selectedTickers,
      direction,
    );
  };

  useImperativeHandle(ref, () => ({
    getFormState: () => ({
      selectedTickers,
      direction,
      selectedExpirations,
      selectedStrikes,
      isValid: isFormValid(),
    }),
    buildScenarios,
    isFormValid,
    resetForm: () => {
      setSelectedTickers([]);
      setDirection(null);
      setSelectedExpirations([]);
      setSelectedStrikes([]);
    },
    loadScenario: (scenario) => {
      const tickerArray = Array.isArray(scenario.tickers)
        ? scenario.tickers
        : (scenario.tickers || '').split(',').map(t => t.trim()).filter(Boolean);
      setSelectedTickers(tickerArray.map(t => ({ value: t, label: t })));

      const positionType = scenario.position_type || 'buy';
      const optionType = scenario.option_type || 'call';
      setDirection(`${positionType}_${optionType}`);

      if (scenario._intermediate) {
        setSelectedExpirations([scenario._intermediate.expiration]);
        setSelectedStrikes([scenario._intermediate.strike]);
      } else {
        // Closest preset match
        let bestExp = null;
        let bestExpDiff = Infinity;
        for (const [key, p] of Object.entries(intermediatePresets.expiration)) {
          const d = Math.abs(p.days_until_exp - scenario.days_until_exp);
          if (d < bestExpDiff) { bestExpDiff = d; bestExp = key; }
        }
        const adjStrike = optionType === 'put' && scenario.strike_pct !== 0 ? -scenario.strike_pct : scenario.strike_pct;
        let bestStrike = null;
        let bestStrikeDiff = Infinity;
        for (const [key, p] of Object.entries(intermediatePresets.strike)) {
          const d = Math.abs(p.strike_pct - adjStrike);
          if (d < bestStrikeDiff) { bestStrikeDiff = d; bestStrike = key; }
        }
        if (bestExp) setSelectedExpirations([bestExp]);
        if (bestStrike) setSelectedStrikes([bestStrike]);
      }
    },
  }));

  const toggle = (arr, setArr, key) => {
    setArr(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleAddToQueue = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddToQueue) {
      scenarios.forEach(s => onAddToQueue(s));
    }
  };

  const handleRunNow = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddAndRun) {
      onAddAndRun(scenarios);
    }
  };

  const handleSave = async () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onSaveScenario) {
      for (const s of scenarios) {
        const ok = await onSaveScenario(s);
        if (!ok) break;
      }
    }
  };

  // Adjust the description sign for put positions (intermediate UI shows
  // the same buckets but the API-side strike is flipped for puts).
  const adjustDescription = (description) => {
    if (!isPut || !description) return description;
    if (description.startsWith('-')) return '+' + description.slice(1);
    if (description.startsWith('+')) return '-' + description.slice(1);
    return description;
  };

  const totalScenarios = isFormValid() ? selectedExpirations.length * selectedStrikes.length : 0;

  return (
    <div className="msf-root">
      <div className="msf-form-header">
        <button
          type="button"
          className="msf-load-preset-btn"
          onClick={() => setIsPresetSheetOpen(true)}
        >
          <BookmarkPlus className="msf-load-preset-btn-icon" />
          Load preset
        </button>
      </div>

      {/* Tickers */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Select Multiple Tickers</h3>
          {selectedTickers.length > 0 ? (
            <span className="msf-section-active">{selectedTickers.length} selected</span>
          ) : (
            <span className="msf-section-count">None selected</span>
          )}
        </div>
        <div className="msf-ticker-input-wrap">
          <MultiSelectTicker
            value={selectedTickers}
            onChange={(tickers) => setSelectedTickers(tickers || [])}
            placeholder="Add ticker..."
            controlBackground="#1f2937"
          />
        </div>
      </div>

      {/* Market direction */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Market direction</h3>
          {direction ? (
            <span className="msf-section-active">{directionLabels[direction].title}</span>
          ) : (
            <span className="msf-section-count">None selected</span>
          )}
        </div>
        <div className="msf-preset-card-grid">
          {Object.entries(directionLabels).map(([key, info]) => {
            const isSelected = direction === key;
            const fullTooltip = info.warning
              ? `${info.warning} — naked calls have unlimited loss potential. Only use if you fully understand the risks.`
              : undefined;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setDirection(key)}
                className={`msf-preset-card ${isSelected ? 'msf-selected' : ''}`}
                title={fullTooltip}
              >
                <span className="msf-preset-card-title">
                  {info.title}
                  {info.warning && (
                    <AlertTriangle
                      style={{ width: 12, height: 12, color: '#f97316', marginLeft: 6, flexShrink: 0 }}
                    />
                  )}
                </span>
                <span className="msf-preset-card-subtitle">{info.subtitle}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Expiration window */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Expiration window</h3>
          <div className="msf-section-meta">
            {selectedExpirations.length > 0 ? (
              <>
                <span className="msf-section-active">
                  {selectedExpirations.length === 1
                    ? intermediatePresets.expiration[selectedExpirations[0]].label
                    : `${selectedExpirations.length} selected`}
                </span>
                <button type="button" className="msf-section-clear" onClick={() => setSelectedExpirations([])}>
                  Clear
                </button>
              </>
            ) : (
              <span className="msf-section-count">None selected</span>
            )}
          </div>
        </div>
        <div className="msf-preset-card-grid">
          {Object.entries(intermediatePresets.expiration).map(([key, preset]) => {
            const isSelected = selectedExpirations.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(selectedExpirations, setSelectedExpirations, key)}
                className={`msf-preset-card ${isSelected ? 'msf-selected' : ''}`}
                title={preset.tooltip}
              >
                <span className="msf-preset-card-title">{preset.label}</span>
                <span className="msf-preset-card-subtitle">{preset.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Strike positioning */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Strike positioning</h3>
          <div className="msf-section-meta">
            {selectedStrikes.length > 0 ? (
              <>
                <span className="msf-section-active">
                  {selectedStrikes.length === 1
                    ? intermediatePresets.strike[selectedStrikes[0]].label
                    : `${selectedStrikes.length} selected`}
                </span>
                <button type="button" className="msf-section-clear" onClick={() => setSelectedStrikes([])}>
                  Clear
                </button>
              </>
            ) : (
              <span className="msf-section-count">None selected</span>
            )}
          </div>
        </div>
        <div className="msf-pill-row-wrap">
          <div className="msf-pill-row" ref={strikeRowRef}>
            {Object.entries(intermediatePresets.strike).map(([key, preset]) => {
              const isSelected = selectedStrikes.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  data-default-pill={key === 'atm' ? 'true' : undefined}
                  onClick={() => toggle(selectedStrikes, setSelectedStrikes, key)}
                  className={`msf-pill ${isSelected ? 'msf-selected' : ''}`}
                  title={preset.tooltip}
                >
                  <span className="msf-pill-label">{preset.label}</span>
                  <span className="msf-pill-sublabel">{adjustDescription(preset.description)}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="msf-strike-caption">
          <span>← in the money</span>
          <span>out of the money →</span>
        </div>
      </div>

      {/* CTA stack: Add to queue / Save as preset / Run X Scenarios */}
      <div className="msf-cta-wrap">
        <button
          type="button"
          className="msf-cta"
          onClick={handleRunNow}
          disabled={!isFormValid() || loading}
        >
          <Play className="msf-cta-icon" style={{ fill: 'currentColor' }} />
          {totalScenarios > 1 ? `Run ${totalScenarios} scenarios` : 'Run scenario'}
        </button>
        <button
          type="button"
          className="msf-cta msf-cta-secondary"
          onClick={handleAddToQueue}
          disabled={!isFormValid() || loading}
        >
          <PlusCircle className="msf-cta-icon" />
          {totalScenarios > 1 ? `Add ${totalScenarios} to queue` : 'Add to queue'}
          {queueCount > 0 && <span className="msf-cta-badge">{queueCount} queued</span>}
        </button>
        <button
          type="button"
          className="msf-save-btn"
          onClick={handleSave}
          disabled={!isFormValid() || loading}
        >
          Save as preset
        </button>
        <p className="msf-cta-hint">
          Tap any pill to toggle. The count in each section header updates as you select;
          "Clear" wipes the section in one tap.
        </p>
      </div>

      {/* Load preset slide-up sheet */}
      <div
        className={`msf-sheet-backdrop ${isPresetSheetOpen ? 'msf-sheet-open' : ''}`}
        onClick={() => setIsPresetSheetOpen(false)}
      />
      <div className={`msf-sheet ${isPresetSheetOpen ? 'msf-sheet-open' : ''}`}>
        <div className="msf-sheet-handle-bar" />
        <div className="msf-sheet-header">
          <span className="msf-sheet-title">Saved presets</span>
          <button
            type="button"
            className="msf-sheet-close"
            onClick={() => setIsPresetSheetOpen(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="msf-sheet-body">
          <ScreenerSavedParams
            savedParams={savedParams}
            onLoad={(p) => { if (onLoadParam) onLoadParam(p); setIsPresetSheetOpen(false); }}
            onDelete={onDeleteParam}
            onAddToQueue={(p) => { if (onAddParamToQueue) onAddParamToQueue(p); setIsPresetSheetOpen(false); }}
            compact
          />
        </div>
      </div>
    </div>
  );
});

export default MobileScreenerIntermediateForm;
